import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { UpgradeOption, UpgradeOptionsResponse } from '../types';

/**
 * Outcome of the deep link's last resolution attempt.
 *
 * - 'idle'         No `?upgrade=` param, or one is present but `options`
 *                  hasn't loaded yet (still waiting to resolve). Callers
 *                  must render nothing for this state — it's what a plain
 *                  /billing visit with no param looks like too.
 * - 'opened'       Matched an eligible, non-pending subscription; onSelect fired.
 * - 'no_match'     The id in the param isn't in `options.subscriptions` at
 *                  all — stale link, wrong org, or the backend excluded it
 *                  (suspended/archived/cancelled/etc — see
 *                  /billing/upgrade-options).
 * - 'not_eligible' Matched, but the subscription has no eligible plans.
 * - 'pending'      Matched, but already has an upgrade request in flight.
 */
export type UpgradeDeepLinkStatus = 'idle' | 'opened' | 'no_match' | 'not_eligible' | 'pending';

/**
 * Opens the upgrade modal for `?upgrade=<subscription_id>`.
 *
 * The over-limit email and the portal's device-limit notices both link here,
 * so a customer lands on /billing with the right modal already open instead of
 * hunting for their subscription's row.
 *
 * Fires at most once per mount: clearing the param below drives `target`
 * back to falsy, and the `!target` check is what keeps it from firing again
 * — no ref needed. That only holds because every caller passes a stable
 * `onSelect` (the three billing cards pass their `useState` setter directly,
 * never a fresh closure), so the effect never reruns on a stale `target`
 * before the clear commits. If a future caller passes an unstable callback,
 * revisit this.
 *
 * Returns a status discriminant so callers can tell a silent dead end (the
 * link matched nothing, or matched something the customer can't upgrade)
 * from a normal idle/opened visit and show a line of explanatory copy.
 */
export function useUpgradeDeepLink(
  options: UpgradeOptionsResponse | undefined,
  onSelect: (option: UpgradeOption) => void
): UpgradeDeepLinkStatus {
  const [searchParams, setSearchParams] = useSearchParams();
  const target = searchParams.get('upgrade');
  const [status, setStatus] = useState<UpgradeDeepLinkStatus>('idle');

  useEffect(() => {
    if (!target) return;
    // Options still loading — keep the param so this can fire once they land.
    if (!options) return;

    const match = options.subscriptions.find((o) => o.subscription_id === target);
    let outcome: UpgradeDeepLinkStatus;
    if (!match) {
      outcome = 'no_match';
    } else if (match.pending_request) {
      // A pending request already renders its own badge on the card; reopening
      // the modal over it would offer an upgrade the customer just asked for.
      outcome = 'pending';
    } else if (match.eligible_plans.length === 0) {
      outcome = 'not_eligible';
    } else {
      outcome = 'opened';
      onSelect(match);
    }

    // Microtask defer keeps setStatus off the effect's synchronous path
    // (react-hooks/set-state-in-effect) — see usePasskeys.ts for the same idiom.
    void Promise.resolve().then(() => setStatus(outcome));

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('upgrade');
        return next;
      },
      { replace: true }
    );
  }, [target, options, onSelect, setSearchParams]);

  return status;
}

/**
 * Explanatory copy for a deep link that landed somewhere other than 'opened'.
 * Returns null for 'idle'/'opened', where callers must render nothing.
 *
 * 'pending' gets its own tone/copy — the card already shows an "Upgrade
 * requested" badge for that subscription, so this isn't an error, just a
 * pointer to where the status lives.
 */
export function upgradeDeepLinkNotice(
  status: UpgradeDeepLinkStatus
): { message: string; tone: 'error' | 'info' } | null {
  switch (status) {
    case 'pending':
      return {
        message: 'You already have an upgrade request in progress for that service — see the request status below.',
        tone: 'info',
      };
    case 'no_match':
    case 'not_eligible':
      return {
        message: "We couldn't open an upgrade for that service. Contact support@cloudunifi.com.",
        tone: 'error',
      };
    default:
      return null;
  }
}
